import request from '@/modules/base/util/request'
import qs from 'qs'
import download from 'downloadjs'
import config from './config'
import lang from 'lang'
import { toast } from '@/modules/base/components/toast/toastConfig'

const { endpoints } = config

const oAuthAccessError = (error, shouldThrow = true) => {
  if (error && (error.meta?.refreshToken?.[0].code === 'EXPIRED'
  || error.meta?.accessToken?.[0].code === 'EXPIRED'
  || error.meta?.oAuthAccess?.[0].code === 'NOT_FOUND')) {
    toast.showToast({ type: 'error', message: lang.accounting.datev.lostDatevAccessErrorMessage })
  }

  if (shouldThrow) {
    throw error
  }
}

const DatevService = {

  getAppSettings: () => {
    return request(endpoints.resourceUrl, {
      auth: true,
      isConcurrent: true,
    }).then((response) => {
      const { body: { data } } = response
      return data
    })
  },

  getTransferList: (collaborationId) => {
    const headers = {}
    if (collaborationId) headers['collaboration-id'] = collaborationId

    return request(endpoints.resourceUrl + '/transfers?limit=100', {
      auth: true,
      isConcurrent: true,
      headers,
    }).then((response) => {
      const { body: { data } } = response
      return data
    })
  },

  /**
 * @typedef {Object} TransferInfoResponseMeta
 * @property {number} count of all document
 */

  /**
 * @typedef {Object} TransferInfoResponse
 * @property {Object} data
 * @property {TransferInfoResponseMeta} meta
 */

  /**
 * @typedef {Object} TransferInfoRequest
 * @property {string} date (API_FORMAT)
 * @property {boolean} resendExisting
 * @property {boolean} useDocumentType
 * @property {string[]} documentTypes
 * @property {string[]} content
 */

  /**
   * get the info what will be transferred
   * @param {TransferInfoRequest} data
   * @returns {Promise<TransferInfoResponse>} response
   */
  getTransferInfo: (data) => {
    return request(endpoints.resourceUrl + '/transfers/info?' + qs.stringify(data), {
      auth: true,
      isConcurrent: true,
    }).then((response) => {
      const { body } = response
      return body
    }).catch(() => toast.showToast({ type: 'error', message: lang.accounting.datev.getTransferInfoErrorMessage }))
  },

  /**
   * download transferred document
   * @param {TransferInfoRequest} data
   * @returns {Promise<TransferInfoResponse>} response
   */
  downloadTransferredDocument: (id) => {
    return request(endpoints.resourceUrl + `/transfers/${id}/download`, {
      responseType: 'arraybuffer',
      auth: true,
      isConcurrent: true,
    }).then((response) => {
      download(response.body, response.response.headers.filename, response.response.headers['content-type'])
    })
  },

  /**
   * @param {string} clientId
   * @param {TransferInfoRequest} data
   * @returns
   */
  postTransfer: (clientId, data) => {
    return request(endpoints.resourceUrl + '/transfers', {
      method: 'POST',
      auth: true,
      isConcurrent: true,
      data: {
        clientId,
        ...data,
      },
    }).then((response) => {
      const { body: { data } } = response
      return data
    }).catch(({ body: error }) => oAuthAccessError(error, true))
  },

  getClient: (clientId) => {
    return request(endpoints.resourceUrl + '/client/' + clientId, {
      auth: true,
      timeout: 20000,
      isConcurrent: true,
    }).then((response) => {
      const { body: { data } } = response
      return data
    }).catch(({ body: error }) => {
      oAuthAccessError(error, false)
      if (error && (error.meta?.clientId?.[0].code === 'NOT_FOUND')) {
        toast.showToast({ type: 'error', message: lang.accounting.datev.clientNotExistsErrorMessage })
      } else {
        toast.showToast({ type: 'error', message: lang.accounting.datev.getClientsErrorMessage })
      }
    })
  },

  revoke: async(action) => {
    return request(endpoints.revoke, { method: 'POST', data: { action }, auth: true, isConcurrent: true })
  },

  getClients: () => {
    return request(endpoints.resourceUrl + '/clients', {
      auth: true,
      timeout: 20000,
      isConcurrent: true,
    }).then((response) => {
      const { body: { data } } = response
      return data
    }).catch(({ body: error }) => {
      oAuthAccessError(error, false)
      toast.showToast({ type: 'error', message: lang.accounting.datev.getClientsErrorMessage })
    })
  },

  getProtocol: (exportId) => {
    return request(endpoints.resourceUrl + '/protocol/' + exportId, {
      auth: true,
      isConcurrent: true,
    }).then((response) => {
      const { body: { data } } = response
      return data
    }).catch(() => toast.showToast({ type: 'error', message: lang.accounting.getProtocolErrorMessage }))
  },

  updateAppSettings: (data) => {
    return request(endpoints.resourceUrl, {
      method: 'POST',
      auth: true,
      isConcurrent: true,
      data,
    }).then((response) => {
      const { body: { data } } = response
      return data
    }).catch(() => toast.showToast({ type: 'error', message: lang.accounting.saveSettingsErrorMessage }))
  },
}

export default DatevService
